
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { AuthContext } from "global/context/AuthContext";
import { getBucketEmailQr } from "api/APIStrichCalls";
import QRCodePopup from "global/components/QRCodePopup";
import { GlobalIconBase } from "global/strich_icons/StrichIcons";

import "./BucketEmailButton.css";



// Show the reusable QR popup for the authenticated account's email upload address
const BucketEmailButton = () => {

    // Keep the bucket-specific API request outside the generic QR popup
    const { token } = useContext(AuthContext);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const triggerRef = useRef(null);

    // Request the current account details only while the popup is open
    useEffect(() => {

        if (!isOpen) return;
        let active = true;

        // Reset the display before retrieving the account's QR details
        setLoading(true);
        setDetails(null);
        setErrorMessage("");

        // Load the upload address and QR image using normal account authentication
        const loadDetails = async () => {

            try {

                const response = await getBucketEmailQr(token);
                if (!active) return;

                // Display the API's result or its actual failure message
                if (response.success) {
                    setDetails(response.results);
                } else {
                    setErrorMessage(response.error_message);
                }

            } catch (error) {

                // Report network failures without replacing the account's stored QR
                if (active) setErrorMessage("Unable to retrieve email upload details.");

            } finally {

                if (active) setLoading(false);

            }

        };

        loadDetails();

        // Ignore responses received after the popup closes
        return () => { active = false; };

    }, [isOpen, token]);



    // Close the popup and restore focus to the gallery button
    const closePopup = useCallback(() => {

        setIsOpen(false);
        triggerRef.current?.focus();

    }, []);



    // Start loading before the popup appears so no stale QR details are displayed
    const openPopup = () => {

        setLoading(true);
        setIsOpen(true);

    };



    // Display a compact gallery action and the shared QR popup
    return (

        <>
            <button ref={triggerRef} type="button" className="bucket-email-button" title="Upload images by email" onClick={openPopup}>
                <GlobalIconBase size={17} strokeWidth={1.8}>
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                </GlobalIconBase>
                <span>Email uploads</span>
            </button>

            <QRCodePopup
                isOpen={isOpen}
                onClose={closePopup}
                loading={loading}
                errorMessage={errorMessage}
                imageUrl={details?.image_url}
                value={details?.email}
                valueLabel="Email address"
                title="Email Image Uploads"
                description="Email images to this address to add them automatically to your bucket."
                filename="bucket-email-qr.png"
            />
        </>

    );

};

export default BucketEmailButton;
